import { useState, useEffect, type FC } from 'react'
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Loader2, Sparkles, Pencil, Save, Wand2, BookmarkPlus } from 'lucide-react'
import api from '@/lib/axios'
import { projectsApi } from '@/api/projects'
import { fieldTemplatesApi } from '@/api/fieldTemplates'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { ApplyTemplateDialogContent } from '@/components/templates/TemplatePicker'

interface Field {
  id: string
  project_id: string
  name: string
  display_name: string
  description: string | null
  field_type: string
  extraction_prompt: string | null
  is_required: boolean
  is_line_item: boolean
  sort_order: number
  created_at: string
}

interface FieldFormData {
  name: string
  display_name: string
  description: string
  field_type: string
  extraction_prompt: string
  is_required: boolean
  is_line_item: boolean
}

interface Props {
  projectId: string
}

const FIELD_TYPES = ['text', 'number', 'date', 'boolean', 'list']

const emptyForm: FieldFormData = {
  name: '',
  display_name: '',
  description: '',
  field_type: 'text',
  extraction_prompt: '',
  is_required: false,
  is_line_item: false,
}

export default function FieldManager({ projectId }: Props) {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [suggesting, setSuggesting] = useState(false)
  const [autoGenerated, setAutoGenerated] = useState(false)
  const [formData, setFormData] = useState<FieldFormData>({ ...emptyForm })

  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['fields', projectId],
    queryFn: () =>
      api.get<Field[]>('/fields', { params: { project_id: projectId } }).then((r) => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (data: FieldFormData) =>
      api.post<Field>('/fields', data, { params: { project_id: projectId } }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields', projectId] })
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<FieldFormData> }) =>
      api.patch<Field>(`/fields/${id}`, data).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields', projectId] })
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/fields/${id}`).then((r) => r.data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['fields', projectId] }),
  })

  // ── Templates ──────────────────────────────────────────────────────────
  const [showApplyDialog, setShowApplyDialog] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [saveAsName, setSaveAsName] = useState('')
  const [saveAsDescription, setSaveAsDescription] = useState('')
  const [applyError, setApplyError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const openApplyDialog = () => {
    setApplyError(null)
    setShowApplyDialog(true)
  }
  const openSaveDialog = () => {
    setSaveError(null)
    setSaveAsName('')
    setSaveAsDescription('')
    setShowSaveDialog(true)
  }

  const applyTemplateMutation = useMutation({
    mutationFn: ({ templateId, replaceExisting }: { templateId: string; replaceExisting: boolean }) =>
      fieldTemplatesApi.applyToProject(projectId, templateId, replaceExisting),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fields', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setShowApplyDialog(false)
    },
    onError: (e: any) => {
      setApplyError(e?.response?.data?.detail || 'Failed to apply template. Please try again.')
    },
  })

  const saveAsTemplateMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      fieldTemplatesApi.saveAsTemplate(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['field-templates'] })
      setShowSaveDialog(false)
      setSaveAsName('')
      setSaveAsDescription('')
    },
    onError: (e: any) => {
      setSaveError(e?.response?.data?.detail || 'Failed to save template. Please try again.')
    },
  })

  // Project context prompt
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId),
  })
  const [projectPrompt, setProjectPrompt] = useState('')
  const [promptSaved, setPromptSaved] = useState(false)

  useEffect(() => {
    if (project?.system_prompt != null) setProjectPrompt(project.system_prompt)
  }, [project?.system_prompt])

  const promptMutation = useMutation({
    mutationFn: (system_prompt: string) =>
      api.patch(`/projects/${projectId}`, { system_prompt: system_prompt || null }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setPromptSaved(true)
      setTimeout(() => setPromptSaved(false), 2000)
    },
  })

  const resetForm = () => {
    setShowForm(false)
    setEditingId(null)
    setAutoGenerated(false)
    setFormData({ ...emptyForm })
  }

  const startEdit = (field: Field) => {
    setEditingId(field.id)
    setShowForm(true)
    setAutoGenerated(false)
    setFormData({
      name: field.name,
      display_name: field.display_name,
      description: field.description || '',
      field_type: field.field_type,
      extraction_prompt: field.extraction_prompt || '',
      is_required: field.is_required,
      is_line_item: field.is_line_item,
    })
  }

  const handleSave = () => {
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const generateInstructions = async () => {
    if (!formData.name || formData.name.length < 2) return
    setSuggesting(true)
    try {
      const res = await api.post('/fields/suggest-prompt', {
        field_name: formData.name,
        field_type: formData.field_type,
        field_description: formData.description,
      })
      setFormData((prev) => ({ ...prev, extraction_prompt: res.data.suggested_prompt }))
      setAutoGenerated(true)
    } catch {
      // Silently fail
    } finally {
      setSuggesting(false)
    }
  }

  const setField = (key: keyof FieldFormData, value: string | boolean) =>
    setFormData((prev) => ({ ...prev, [key]: value }))

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Extraction Fields</h2>
          <p className="text-sm text-muted-foreground">
            Define what data to extract from your documents
          </p>
        </div>
        {!showForm && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={openApplyDialog}>
              <Wand2 className="h-4 w-4 mr-1" />
              Apply Template
            </Button>
            {fields.length > 0 && (
              <Button
                variant="outline"
                onClick={openSaveDialog}
                title="Save current fields as a reusable template"
              >
                <BookmarkPlus className="h-4 w-4 mr-1" />
                Save as Template
              </Button>
            )}
            <Button onClick={() => { resetForm(); setShowForm(true) }}>
              <Plus className="h-4 w-4 mr-1" />
              Add Field
            </Button>
          </div>
        )}
      </div>

      {/* Project Context Prompt */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Project Context</Label>
              <p className="text-xs text-muted-foreground">
                Provide context about your documents. This is included in every extraction prompt for all fields.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {promptSaved && <span className="text-xs text-green-600">Saved</span>}
              <Button
                size="sm"
                variant="outline"
                onClick={() => promptMutation.mutate(projectPrompt)}
                disabled={promptMutation.isPending || projectPrompt === (project?.system_prompt || '')}
              >
                {promptMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Save className="h-3 w-3 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
          <Textarea
            placeholder='e.g., "These are Australian commercial leases governed by NSW law. Dates follow DD/MM/YYYY format in source documents."'
            value={projectPrompt}
            onChange={(e) => setProjectPrompt(e.target.value)}
            rows={2}
            className="text-sm"
          />
        </CardContent>
      </Card>

      {/* Document Summary Prompt */}
      <SummaryPromptCard projectId={projectId} project={project} fields={fields} queryClient={queryClient} />

      {/* Add / Edit field form */}
      {showForm && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="text-base">
              {editingId ? 'Edit Field' : 'New Field'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Field Name *</Label>
                <Input
                  placeholder="e.g., Contract Value"
                  value={formData.name}
                  onChange={(e) => {
                    setField('name', e.target.value)
                    if (!editingId) setField('display_name', e.target.value)
                    setAutoGenerated(false)
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Display Name</Label>
                <Input
                  placeholder="Shown in results table"
                  value={formData.display_name}
                  onChange={(e) => setField('display_name', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Field Type</Label>
                <Select
                  value={formData.field_type}
                  onValueChange={(v) => setField('field_type', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  placeholder="What this field represents"
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Extraction Instructions</Label>
                <div className="flex items-center gap-2">
                  {autoGenerated && (
                    <Badge variant="secondary" className="text-xs gap-1">
                      <Sparkles className="h-3 w-3" />
                      AI-generated
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (confirm('Generate extraction instructions using AI?')) {
                        generateInstructions()
                      }
                    }}
                    disabled={suggesting || !formData.name || formData.name.length < 2}
                    title=""
                  >
                    {suggesting ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Generating...</>
                    ) : (
                      <><Sparkles className="h-3 w-3 mr-1" /> Generate with AI</>
                    )}
                  </Button>
                </div>
              </div>
              <Textarea
                placeholder="Write your own instructions or use 'Generate with AI' above..."
                value={formData.extraction_prompt}
                onChange={(e) => {
                  setField('extraction_prompt', e.target.value)
                  setAutoGenerated(false)
                }}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use the default extraction prompt. Use "Generate with AI" to auto-create instructions (uses AI generation).
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={formData.is_required}
                  onChange={(e) => setField('is_required', e.target.checked)}
                />
                <Label htmlFor="is_required" className="cursor-pointer">Required field</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_line_item"
                  checked={formData.is_line_item}
                  onChange={(e) => setField('is_line_item', e.target.checked)}
                />
                <Label htmlFor="is_line_item" className="cursor-pointer">Repeating (line item)</Label>
              </div>
            </div>
            {formData.is_line_item && (
              <p className="text-xs text-muted-foreground">
                Check this for fields that can have multiple values per document (e.g., product names, line amounts).
              </p>
            )}

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={!formData.name || isSaving}>
                {isSaving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Field'}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fields List */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading fields...</div>
      ) : fields.length === 0 && !showForm ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground mb-4">No fields defined yet</p>
          <Button onClick={() => { resetForm(); setShowForm(true) }}>
            <Plus className="h-4 w-4 mr-1" />
            Add your first field
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field, idx) => (
            <Card key={field.id} className={editingId === field.id ? 'border-primary/50' : ''}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-sm text-muted-foreground w-5 shrink-0">{idx + 1}.</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{field.display_name || field.name}</span>
                      <Badge variant="outline" className="text-xs">{field.field_type}</Badge>
                      {field.is_line_item && <Badge variant="secondary" className="text-xs">Line Item</Badge>}
                      {field.is_required && <Badge variant="secondary" className="text-xs">Required</Badge>}
                    </div>
                    {field.description && (
                      <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                    )}
                    {field.extraction_prompt && (
                      <p className="text-xs text-muted-foreground mt-1 max-w-lg truncate">
                        {field.extraction_prompt}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => startEdit(field)}
                    title="Edit field"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      if (confirm('Delete this field?')) deleteMutation.mutate(field.id)
                    }}
                    title="Delete field"
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Apply Template Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={(open) => { if (!open) setShowApplyDialog(false) }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Apply Field Template</DialogTitle>
          </DialogHeader>
          <ApplyTemplateDialogContent
            hasExistingFields={fields.length > 0}
            isApplying={applyTemplateMutation.isPending}
            error={applyError}
            onCancel={() => setShowApplyDialog(false)}
            onApply={(templateId, replaceExisting) =>
              applyTemplateMutation
                .mutateAsync({ templateId, replaceExisting })
                .catch(() => {
                  // Error already captured by onError; swallow rejection here to
                  // prevent unhandled-promise warnings in the dialog component.
                })
            }
          />
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={(open) => { if (!open) setShowSaveDialog(false) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save fields as Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Snapshot this project's {fields.length} field{fields.length !== 1 ? 's' : ''} as a reusable template
              for your organization.
            </p>
            {saveError && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {saveError}
              </div>
            )}
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                placeholder="e.g., Q4 Vendor Invoice Schema"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={saveAsDescription}
                onChange={(e) => setSaveAsDescription(e.target.value)}
                placeholder="What kind of documents is this template for?"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button
              disabled={!saveAsName.trim() || saveAsTemplateMutation.isPending}
              onClick={() =>
                saveAsTemplateMutation.mutate({
                  name: saveAsName.trim(),
                  description: saveAsDescription.trim() || undefined,
                })
              }
            >
              {saveAsTemplateMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving...</>
              ) : (
                'Save Template'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}


function SummaryPromptCard({ projectId, project, fields, queryClient }: {
  projectId: string
  project: { summary_prompt?: string | null; system_prompt?: string | null } | undefined
  fields: { name: string; display_name: string; is_line_item: boolean }[]
  queryClient: QueryClient
}) {
  const [summaryPrompt, setSummaryPrompt] = useState('')
  const [summarySaved, setSummarySaved] = useState(false)
  const [generating, setGenerating] = useState(false)

  useEffect(() => {
    if (project?.summary_prompt != null) setSummaryPrompt(project.summary_prompt)
  }, [project?.summary_prompt])

  const summaryMutation = useMutation({
    mutationFn: (summary_prompt: string) =>
      api.patch(`/projects/${projectId}`, { summary_prompt: summary_prompt || null }).then((r: { data: unknown }) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      setSummarySaved(true)
      setTimeout(() => setSummarySaved(false), 2000)
    },
  })

  const generateSummaryPrompt = async () => {
    setGenerating(true)
    try {
      const fieldNames = fields.map(f => `${f.display_name || f.name}${f.is_line_item ? ' (line item)' : ''}`).join(', ')
      const context = project?.system_prompt || 'General document extraction'

      const res = await api.post('/fields/suggest-prompt', {
        field_name: 'Document Summary',
        field_type: 'text',
        field_description: `Generate a summary prompt instruction for documents in this project. The project context is: "${context}". The extraction fields are: ${fieldNames}. Write a clear instruction (2-4 sentences) telling an AI how to summarize each document after extraction, focusing on the most important extracted data points.`,
        project_context: context,
      })
      setSummaryPrompt(res.data.suggested_prompt)
    } catch {
      // silently fail
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Document Summary Prompt</Label>
            <p className="text-xs text-muted-foreground">
              When set, a per-document summary will be generated after extraction using this prompt. Summary is generated using AI after each extraction run.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {summarySaved && <span className="text-xs text-green-600">Saved</span>}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (confirm('Generate a summary prompt using AI?')) {
                  generateSummaryPrompt()
                }
              }}
              disabled={generating || fields.length === 0}
            >
              {generating ? (
                <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Generating...</>
              ) : (
                <><Sparkles className="h-3 w-3 mr-1" /> Generate with AI</>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => summaryMutation.mutate(summaryPrompt)}
              disabled={summaryMutation.isPending || summaryPrompt === (project?.summary_prompt || '')}
            >
              {summaryMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Save className="h-3 w-3 mr-1" />
              )}
              Save
            </Button>
          </div>
        </div>
        <Textarea
          placeholder='e.g., "Summarize this document in 2-3 sentences, highlighting the key parties, dates, and financial terms."'
          value={summaryPrompt}
          onChange={(e) => setSummaryPrompt(e.target.value)}
          rows={2}
          className="text-sm"
        />
      </CardContent>
    </Card>
  )
}
